import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CancelIcon from "@mui/icons-material/Cancel";
import { adminAxios } from "../../axiosInstance";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";
import loader from "../../assets/loader.webp";
import ExpensesFilter from "./ExpensesFilter";

const CATEGORY_OPTIONS = [
  { value: "Personnel", label: "Personnel" },
  { value: "Facility", label: "Facility" },
  { value: "Marketing", label: "Marketing" },
  { value: "Technology", label: "Technology" },
  { value: "Inventory", label: "Inventory" },
  { value: "Compliance", label: "Compliance" },
  { value: "Others", label: "Others" },
];

const TYPE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const CATEGORY_INFO = [
  "Personnel: Labor costs including staff salaries and benefits.",
  "Facility: Costs related to physical space, including rent, leases, utilities, property taxes, and routine repairs.",
  "Marketing: All costs related to generating sales, including advertising, sales commissions, and promotional materials.",
  "Technology: Software subscriptions and IT equipment.",
  "Inventory: Cost of products and raw materials.",
  "Finance: Interest paid on business loans and bank fees.",
  "Compliance: Legal and professional fees related to regulatory requirements.",
];

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })}`;
};

function Expenses() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [page, setPage] = useState(1);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const itemsPerPage = 10;

  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addtab, setAddtab] = useState(false);
  const [edittab, setEdittab] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [editId, setEditId] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [error, setError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  const [form, setForm] = useState({
    name: "",
    category: "",
    type: "monthly",
    amount: "",
    description: "",
    expense_date: new Date().toISOString().split('T')[0],
  });

  // ============================================
  // FETCHING LOGIC & FILTERING
  // ============================================
  const fetchExpenses = async (filter = dateFilter) => {
    try {
      // Only the date range is filtered server-side; the rest is applied in the list below
      const dateParams =
        filter?.start_date && filter?.end_date
          ? { start_date: filter.start_date, end_date: filter.end_date }
          : undefined;
      setIsLoading(true);
      const token = localStorage.getItem("myToken");
      const res = await adminAxios.get("expenses", {
        headers: { Authorization: `Bearer ${token}` },
        params: dateParams,
      });

      // Check localStorage if action is "add" then open add modal
      const action = localStorage.getItem("action");
      if (action === "add") {
        setAddtab(true);
        localStorage.removeItem("action");
      }

      let data = res.data?.data || [];
      setExpenses(data);
    } catch (err) {
      console.error("Failed to fetch expenses", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // On expenses or search change, filter and reset to first page if needed
    const q = searchData.toLowerCase();
    const { category, type, min_amount, max_amount } = dateFilter || {};
    const result = expenses.filter((exp) => {
      const amount = Number(exp.amount);
      return (
        (exp.name.toLowerCase().includes(q) || exp.category.toLowerCase().includes(q)) &&
        (!category || exp.category?.toLowerCase() === category.toLowerCase()) &&
        (!type || exp.type?.toLowerCase() === type) &&
        (min_amount === "" || min_amount == null || amount >= Number(min_amount)) &&
        (max_amount === "" || max_amount == null || amount <= Number(max_amount))
      );
    });
    setFilteredExpenses(result);
    setPage(1); // reset page on search/filter
  }, [expenses, searchData, dateFilter]);

  // ============================================
  // PAGINATION LOGIC
  // ============================================
  const paginated = filteredExpenses.slice(
    (page - 1) * itemsPerPage, 
    page * itemsPerPage
  );

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    setSearchData(e.target.value);
  };

  const handleAdd = () => {
    setAddtab(true);
    setEdittab(false);
    const today = new Date().toISOString().split('T')[0];
    setForm({
      name: "",
      category: "",
      type: "monthly",
      amount: "",
      description: "",
      expense_date: today,
    });
    setError("");
  };

  const handleEdit = (exp) => {
    setEditId(exp.id);
    setForm({
      name: exp.name,
      category: exp.category,
      type: exp.type,
      amount: exp.amount,
      description: exp.description || "",
      expense_date: exp.expense_date || new Date().toISOString().split('T')[0],
    });
    setEdittab(true);
    setAddtab(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      if (editId) {
        await adminAxios.put(`expenses/${editId}`, form);
      } else {
        await adminAxios.post("expenses", form);
      }
      await fetchExpenses();
      setAddtab(false);
      setEdittab(false);
      setEditId(null);
    } catch (e) {
      setError(editId ? "Failed to update expense." : "Failed to add expense.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (exp) => {
    setExpenseToDelete(exp);
    setIsModalOpen(true);
    setDeleteError("");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setExpenseToDelete(null);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await adminAxios.delete(`expenses/${expenseToDelete.id}`);
      await fetchExpenses();
      handleCloseModal();
    } catch (e) {
      setDeleteError("Failed to delete expense.");
    } finally {
      setDeleting(false);
    }
  };

  const handleApplyFilter = (form) => {
    setDateFilter(form);
    setShowFilter(false);
    fetchExpenses(form);
  };

  const handleClearFilter = () => {
    setDateFilter(null);
    setShowFilter(false);
    fetchExpenses(null);
  };

  const handleOpenInfo = () => setInfoOpen(true);
  const handleCloseInfo = () => setInfoOpen(false);

  // Fix a possible bug: close edit form with X in edit modal
  const handleCloseEdit = () => {
    setEdittab(false);
    setEditId(null);
  };

  // Use original name for Add/Edit expense submit handler if present in form
  const handleAddOrEditExpense = handleSubmit;

  return (
    <div className="lg:overflow-y-hidden">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-center p-4 w-full">
        <h3 className="text-xl font-semibold">
          <ReceiptIcon /> Expenses
        </h3>
        <div className="flex gap-3">
          <button
            onClick={handleOpenInfo}
            className="p-3 rounded-lg text-white bg-red-500"
          >
            <InfoOutlinedIcon />
          </button>
          <button
            onClick={handleAdd}
            className="p-3 rounded-lg text-white bg-blue-500"
          >
            Add Expense <AddIcon />
          </button>
        </div>
      </div>

      {/* Add Expense Form */}
      {addtab && !edittab && (
        <div className="bg-white p-6 rounded-md">
          <div className="flex justify-end">
            <CancelIcon
              className="cursor-pointer"
              onClick={() => setAddtab(false)}
            />
          </div>
          <h3 className="text-2xl font-medium text-center">Add New Expense</h3>
          <div className="py-12 flex flex-col justify-center items-center gap-6">
            <div className="flex flex-col flex-wrap justify-center lg:flex-row gap-6">
              <div className="flex flex-col">
                <label>Name</label>
                <input
                  className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none lg:w-[400px]"
                  placeholder="Name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="flex flex-col lg:w-[400px]">
                <label>Category</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                  className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                  required
                >
                  <option value="">Select</option>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col lg:w-[400px]">
                <label>Type</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleFormChange}
                  className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                  required
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label>Amount</label>
                <input
                  className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none lg:w-[400px]"
                  placeholder="Amount"
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex flex-col">
                <label>Description</label>
                <input
                  className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none lg:w-[400px]"
                  placeholder="Description"
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            {error && <p className="text-red-600">{error}</p>}
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-3 text-white rounded-lg w-full lg:w-[400px] bg-blue-500 hover:bg-blue-600"
            >
              {saving ? "Adding Expense..." : "Add Expense"}
            </button>
          </div>
        </div>
      )}

      {/* Edit Expense Form */}
      {edittab && !addtab && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-end">
              <CancelIcon
                className="cursor-pointer text-gray-600 hover:text-gray-800"
                onClick={handleCloseEdit}
              />
            </div>
            <h3 className="text-2xl font-medium text-center mb-2">
              {editId ? "Edit Expense" : "Add New Expense"}
            </h3>
            <div className="py-8 flex flex-col justify-center items-center gap-6">
              <div className="flex flex-col flex-wrap justify-center lg:flex-row gap-6 w-full">
                <div className="flex flex-col w-full lg:w-[400px]">
                  <label className="mb-2 text-gray-700 font-medium">Expense Name</label>
                  <input
                    className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                    placeholder="e.g. Fuel, Internet, Office supplies"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="flex flex-col w-full lg:w-[400px]">
                  <label className="mb-2 text-gray-700 font-medium">Category</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleFormChange}
                    className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Category</option>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col w-full lg:w-[400px]">
                  <label className="mb-2 text-gray-700 font-medium">Type</label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleFormChange}
                    className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                    required
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col w-full lg:w-[400px]">
                  <label className="mb-2 text-gray-700 font-medium">Amount</label>
                  <input
                    className="border-2 border-gray-300 p-2 rounded-md w-full focus:border-teal-500 focus:outline-none"
                    placeholder="Amount"
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleFormChange}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {error && <p className="text-red-600 text-center">{error}</p>}

              <button
                onClick={handleAddOrEditExpense}
                disabled={saving}
                className="px-4 py-3 text-white rounded-lg w-full lg:w-[400px] bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition"
              >
                {saving
                  ? (editId ? "Updating..." : "Adding...")
                  : (editId ? "Update Expense" : "Add Expense")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense List Table */}
      {!edittab && !addtab && (
        <div className="w-full p-4 flex flex-col gap-2 bg-white">
          <div className="flex justify-between items-center">
            <input
              placeholder="Search for"
              type="text"
              name="search"
              value={searchData}
              onChange={handleChange}
              className="border-2 border-gray-400 w-60 p-2 px-6"
            />
            <button
              className={`ml-2 flex items-center gap-1 hover:cursor-pointer border px-4 py-2 rounded ${
                dateFilter ? "border-green-600 text-green-700" : "border-black"
              }`}
              onClick={() => setShowFilter(true)}
              aria-label="Filter"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                width="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
                <circle cx="8" cy="6" r="2" />
                <circle cx="16" cy="12" r="2" />
                <circle cx="8" cy="18" r="2" />
              </svg>
            </button>
          </div>
          <div className="w-full">
            {isLoading ? (
              <div className="flex justify-center items-center">
                <img src={loader} alt="loader" className="w-10 lg:w-20" />
              </div>
            ) : (
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="py-2 px-4 border-b border-gray-300">NAME</th>
                    <th className="py-2 px-4 border-b border-gray-300">CATEGORY</th>
                    <th className="py-2 px-4 border-b border-gray-300">TYPE</th>
                    <th className="py-2 px-4 border-b border-gray-300">AMOUNT</th>
                    <th className="py-2 px-4 border-b border-gray-300">DESCRIPTION</th>
                    <th className="py-2 px-4 border-b border-gray-300">DATE/TIME</th>
                    <th className="py-2 px-4 border-b border-gray-300 text-wrap">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated?.map((item) => (
                    <tr
                      key={item?.id}
                      className="hover:bg-gray-100 border-b border-gray-300"
                    >
                      <td className="py-2 px-4 text-wrap">{item.name}</td>
                      <td className="py-2 px-4 text-center text-wrap capitalize">
                        {item.category}
                      </td>
                      <td className="py-2 px-4 text-center text-wrap capitalize">
                        {item.type}
                      </td>
                      <td className="py-2 px-4 text-center text-wrap">
                        ₦{formatNumbersWithDecimal(item.amount)}
                      </td>
                      <td className="py-2 px-4 text-center text-wrap">
                        {item?.description ? item?.description : "N/A"}
                      </td>
                      <td className="py-2 px-4 text-center text-wrap">
                        {formatDateTime(item?.created_at)}
                      </td>
                      <td className="py-2 flex justify-center gap-3 px-2">
                        <EditIcon
                          onClick={() => handleEdit(item)}
                          className="text-blue-500 cursor-pointer"
                        />
                        <DeleteIcon
                          onClick={() => handleDelete(item)}
                          className="text-red-500 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 px-2 mb-5">
            {/* Previous Button */}
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`px-4 py-3 w-18 rounded-lg font-semibold text-white transition-all duration-200 shadow-md
                ${page === 1 
                  ? "bg-black cursor-not-allowed opacity-50" 
                  : "bg-black hover:bg-gray-900"
                }`}
            >
              Prev
            </button>

            {/* Page Info - Centered */}
            <div className="my-4 sm:my-0 text-gray-700 font-medium">
              Page {page} of {Math.max(1, Math.ceil(filteredExpenses.length / itemsPerPage))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * itemsPerPage >= filteredExpenses.length}
              className={`px-4 py-3 w-18 rounded-lg font-semibold text-white transition-all duration-200 shadow-md
                ${page * itemsPerPage >= filteredExpenses.length
                  ? "bg-black cursor-not-allowed opacity-50"
                  : "bg-black hover:bg-gray-900"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ExpensesFilter
        key={showFilter ? "open" : "closed"}
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        filteredData={dateFilter}
        onApply={handleApplyFilter}
        onClear={handleClearFilter}
      />

      {/* Info Modal */}
      {infoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-end">
              <CancelIcon
                className="cursor-pointer text-gray-600 hover:text-gray-800"
                onClick={handleCloseInfo}
              />
            </div>
            <h3 className="text-2xl font-medium text-center mb-6">
              Expense Category Explanations
            </h3>
            <ul className="space-y-4 text-gray-700">
              {CATEGORY_INFO.map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold text-lg">•</span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-md max-w-md w-full">
            <h3 className="text-2xl font-medium text-center mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-700 text-center mb-6">
              Are you sure you want to delete the expense{" "}
              <strong>{expenseToDelete?.name}</strong>?
            </p>
            {deleteError && (
              <p className="text-red-600 text-center mb-4">{deleteError}</p>
            )}
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleCloseModal}
                disabled={deleting}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 rounded-lg font-medium transition disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;